import { type QueryKey } from "@tanstack/react-query";
import React, { createContext, useContext, useMemo } from "react";

import { SURFACE_KEYS, type SurfaceKey } from "@/schemas/surface";

/**
 * Each screen that renders a list of statuses declares its surface (and, for feed surfaces,
 * the exact React Query key of the served list) by wrapping the list in <SurfaceProvider>.
 * A status is tagged by where it is actually rendered, read from the nearest provider.
 */

// Re-exported so existing UI-layer importers keep their `@/contexts/surface-context` path.
export { SURFACE_KEYS, type SurfaceKey };

export interface SurfaceValue {
  surface: SurfaceKey;
  /**
   * Exact React Query key of the served list, used to resolve absolute position. `null` on
   * non-feed surfaces (status:detail, thread:*) — position is omitted there, event still sent.
   */
  positionQueryKey: QueryKey | null;
}

const SurfaceContext = createContext<SurfaceValue | null>(null);

interface SurfaceProviderProps {
  /** Omitted/undefined = not an instrumented surface; children aren't tracked. */
  surface?: SurfaceKey;
  positionQueryKey?: QueryKey | null;
  children: React.ReactNode;
}

const SurfaceProvider: React.FC<SurfaceProviderProps> = ({
  surface,
  positionQueryKey = null,
  children,
}) => {
  const value = useMemo<SurfaceValue | null>(
    () => (surface ? { surface, positionQueryKey } : null),
    // Stringify so a fresh key-array identity each render doesn't churn the value
    // (and, through it, the tracking effect's deps).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [surface, JSON.stringify(positionQueryKey ?? null)],
  );

  return (
    <SurfaceContext.Provider value={value}>{children}</SurfaceContext.Provider>
  );
};

const useSurface = (): SurfaceValue | null => useContext(SurfaceContext);

export { SurfaceProvider, useSurface };
